import { InfoCircleOutlined } from "@ant-design/icons";
import { InputNumber, Switch, Tooltip } from "antd";
import PropTypes from "prop-types";

/** @typedef {{ disabled: boolean, rate?: number }} RateTier */
/** @typedef {{ hourlyRate: RateTier, dailyRate: RateTier, weeklyRate: RateTier, monthlyRate: RateTier }} RateValue */

/** @type {readonly { key: keyof RateValue, labelKey: string, tooltip: string }[]} */
const RATE_TIERS = [
    {
        key: "hourlyRate",
        labelKey: "rates.perHour",
        tooltip: "Set the rental price for each hour the vehicle is booked.",
    },
    {
        key: "dailyRate",
        labelKey: "rates.perDay",
        tooltip: "Set the rental price for a full 24-hour period.",
    },
    {
        key: "weeklyRate",
        labelKey: "rates.perWeek",
        tooltip: "Set a discounted rate for weekly rentals (7 consecutive days).",
    },
    {
        key: "monthlyRate",
        labelKey: "rates.perMonth",
        tooltip: "Set a discounted rate for monthly rentals (30 consecutive days).",
    },
];

/** @param {RateTier | null | undefined} tier @returns {boolean} */
const isTierEnabled = (tier) => Boolean(tier && !tier.disabled);

/** @param {RateTier | null | undefined} tier @returns {boolean} */
const isTierAmountValid = (tier) => isTierEnabled(tier) && Number(tier.rate) > 0;

/** @returns {RateValue} */
export const createDefaultRate = () => ({
    hourlyRate: { disabled: false },
    dailyRate: { disabled: false },
    weeklyRate: { disabled: false },
    monthlyRate: { disabled: false },
});

/** @param {RateValue | null | undefined} rate @returns {RateValue} */
export const normalizeRateValue = (rate) => ({
    hourlyRate: {
        disabled: rate?.hourlyRate?.disabled ?? true,
        rate: rate?.hourlyRate?.rate,
    },
    dailyRate: {
        disabled: rate?.dailyRate?.disabled ?? true,
        rate: rate?.dailyRate?.rate,
    },
    weeklyRate: {
        disabled: rate?.weeklyRate?.disabled ?? true,
        rate: rate?.weeklyRate?.rate,
    },
    monthlyRate: {
        disabled: rate?.monthlyRate?.disabled ?? true,
        rate: rate?.monthlyRate?.rate,
    },
});

/**
 * Valid when at least one tier is enabled with rate > 0, and every enabled tier has an amount.
 * @param {RateValue | null | undefined} rate
 * @returns {boolean}
 */
export const isRateStepValid = (rate) => {
    const enabledTiers = RATE_TIERS.map(({ key }) => rate?.[key]).filter((tier) => isTierEnabled(tier));

    return enabledTiers.length > 0 && enabledTiers.every((tier) => Number(tier.rate) > 0);
};

/** @param {RateValue} rate */
export const buildRatePayload = (rate) => ({
    hourlyRate: {
        disabled: rate.hourlyRate?.disabled ?? true,
        rate: rate.hourlyRate?.rate || 0,
    },
    dailyRate: {
        disabled: rate.dailyRate?.disabled ?? true,
        rate: rate.dailyRate?.rate || 0,
    },
    weeklyRate: {
        disabled: rate.weeklyRate?.disabled ?? true,
        rate: rate.weeklyRate?.rate || 0,
    },
    monthlyRate: {
        disabled: rate.monthlyRate?.disabled ?? true,
        rate: rate.monthlyRate?.rate || 0,
    },
});

/** @param {RateTier | null | undefined} tier @param {(key: string) => string} t @returns {string} */
const formatTierDisplayValue = (tier, t) => {
    if (!tier || tier.disabled) return t("rates.switchOff");
    if (Number(tier.rate) > 0) return `$${tier.rate}`;
    return "—";
};

/** @param {RateValue | null | undefined} rate @param {(key: string) => string} t @returns {{ label: string, value: string }[]} */
export const getRateDisplayRows = (rate, t) =>
    RATE_TIERS.map(({ key, labelKey }) => ({
        label: t(labelKey),
        value: formatTierDisplayValue(rate?.[key], t),
    }));

/** @param {RateValue | null | undefined} rate @param {(key: string) => string} t @returns {string} */
export const formatRateSummary = (rate, t) => {
    const rows = getRateDisplayRows(rate, t).filter((row) => row.value !== t("rates.switchOff") && row.value !== "—");
    return rows.length ? rows.map((row) => `${row.label}: ${row.value}`).join(", ") : "N/A";
};

/**
 * Controlled hourly/daily/weekly/monthly rate inputs with on/off switches.
 * @param {{ value: RateValue, onChange: (rate: RateValue) => void, t: (key: string) => string }} props
 */
const RateFields = ({ value, onChange, t }) => {
    const updateTier = (key, patch) => {
        onChange({
            ...value,
            [key]: { ...value[key], ...patch },
        });
    };

    return (
        <div className="flex w-full gap-4 flex-wrap">
            {RATE_TIERS.map(({ key, labelKey, tooltip }) => {
                const tier = value[key] || { disabled: true };
                const enabled = isTierEnabled(tier);
                const hasAmountError = enabled && !isTierAmountValid(tier);

                return (
                    <div key={key} className="flex-1 min-w-[140px]">
                        <div className="flex justify-between items-center w-full mb-1">
                            <span className="text-sm font-medium flex items-center gap-1">
                                {t(labelKey)}
                                <Tooltip title={tooltip}>
                                    <InfoCircleOutlined className="text-gray-400" />
                                </Tooltip>
                            </span>
                            <Switch
                                checked={enabled}
                                checkedChildren={t("rates.switchOn")}
                                unCheckedChildren={t("rates.switchOff")}
                                onChange={(checked) =>
                                    updateTier(key, {
                                        disabled: !checked,
                                        ...(checked ? {} : { rate: undefined }),
                                    })
                                }
                                size="small"
                            />
                        </div>
                        <InputNumber
                            prefix="$"
                            className="w-full"
                            disabled={!enabled}
                            min={1}
                            value={tier.rate}
                            status={hasAmountError ? "error" : undefined}
                            onChange={(rateValue) => updateTier(key, { rate: rateValue ?? undefined })}
                        />
                    </div>
                );
            })}
        </div>
    );
};

const rateTierPropType = PropTypes.shape({
    disabled: PropTypes.bool,
    rate: PropTypes.number,
});

RateFields.propTypes = {
    value: PropTypes.shape({
        hourlyRate: rateTierPropType,
        dailyRate: rateTierPropType,
        weeklyRate: rateTierPropType,
        monthlyRate: rateTierPropType,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

const tableInputStyle = {
    height: "1.6rem",
    padding: "0",
    paddingLeft: "0.6rem",
    margin: "0",
    backgroundColor: "white",
    fontSize: "0.9rem",
    width: "100%",
    outline: "none",
    border: 0,
    flex: 1,
};

/**
 * Rate edit rows for bordered car detail edit layout.
 */
export const getRateEditRows = (value, onChange, t) =>
    RATE_TIERS.map(({ key, labelKey }) => {
        const tier = value[key] || { disabled: true };
        const enabled = isTierEnabled(tier);
        const hasAmountError = enabled && !isTierAmountValid(tier);

        return {
            label: t(labelKey),
            content: (
                <div className="flex items-center gap-2">
                    <InputNumber
                        prefix="$"
                        disabled={!enabled}
                        min={1}
                        value={tier.rate}
                        status={hasAmountError ? "error" : undefined}
                        onChange={(rateValue) =>
                            onChange({
                                ...value,
                                [key]: { ...value[key], rate: rateValue ?? undefined },
                            })
                        }
                        className="!h-8 flex-1"
                    />
                    <Switch
                        checked={enabled}
                        checkedChildren={t("rates.switchOn")}
                        unCheckedChildren={t("rates.switchOff")}
                        onChange={(checked) =>
                            onChange({
                                ...value,
                                [key]: {
                                    ...value[key],
                                    disabled: !checked,
                                    ...(checked ? {} : { rate: undefined }),
                                },
                            })
                        }
                        size="small"
                    />
                </div>
            ),
        };
    });

/**
 * Table-row rate fields for car detail edit (label column + control column).
 * @param {{ value: RateValue, onChange: (rate: RateValue) => void, t: (key: string) => string, labelColor?: string }} props
 */
export const RateTableEditRows = ({ value, onChange, t, labelColor = "#121212" }) => {
    const updateTier = (key, patch) => {
        onChange({
            ...value,
            [key]: { ...value[key], ...patch },
        });
    };

    return RATE_TIERS.map(({ key, labelKey }) => {
        const tier = value[key] || { disabled: true };
        const enabled = isTierEnabled(tier);
        const hasAmountError = enabled && !isTierAmountValid(tier);

        return (
            <tr key={key}>
                <td style={{ color: labelColor, width: "10rem" }}>{t(labelKey)}</td>
                <td className="fontSemi">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <InputNumber
                            prefix="$"
                            disabled={!enabled}
                            min={1}
                            value={tier.rate}
                            status={hasAmountError ? "error" : undefined}
                            onChange={(rateValue) => updateTier(key, { rate: rateValue ?? undefined })}
                            style={tableInputStyle}
                        />
                        <Switch
                            checked={enabled}
                            checkedChildren={t("rates.switchOn")}
                            unCheckedChildren={t("rates.switchOff")}
                            onChange={(checked) =>
                                updateTier(key, {
                                    disabled: !checked,
                                    ...(checked ? {} : { rate: undefined }),
                                })
                            }
                            size="small"
                        />
                    </div>
                </td>
            </tr>
        );
    });
};

RateTableEditRows.propTypes = {
    value: PropTypes.shape({
        hourlyRate: rateTierPropType,
        dailyRate: rateTierPropType,
        weeklyRate: rateTierPropType,
        monthlyRate: rateTierPropType,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    labelColor: PropTypes.string,
};

export default RateFields;
